import React from "react";
import { graphql, useMutation } from "react-relay";

import { CUSTOM_VOCABULARY_WORD_LIMIT } from "~/meetingRecorder/constants";

import { useUserSetting } from "./shared";

import type { MeetingCopilotSettingsQuery } from "~/__generated__/MeetingCopilotSettingsQuery.graphql";
import type { useCustomVocabularySettingValidateCustomVocabularyListMutation } from "~/__generated__/useCustomVocabularySettingValidateCustomVocabularyListMutation.graphql";

const validateCustomVocabularyListMutation = graphql`
	mutation useCustomVocabularySettingValidateCustomVocabularyListMutation($customVocabularyList: [String!]!) {
		validateCustomVocabularyList(customVocabularyList: $customVocabularyList) {
			valid
			error
		}
	}
`;
export const useCustomVocabularySetting = (viewer: MeetingCopilotSettingsQuery["response"]["viewer"]) => {
	const [isCustomVocabularyEnabled, setIsCustomVocabularyEnabled] = useUserSetting(
		"customVocabularyEnabled",
		viewer.settings.customVocabularyEnabled,
		{
			updater: true,
		},
	);

	const [customVocabularyList, setCustomVocabularyList] = useUserSetting(
		"customVocabularyList",
		viewer.settings.customVocabularyList ?? [],
		{
			updater: true,
		},
	);

	const [validateCustomVocabularyList, isValidatingCustomVocabularyList] =
		useMutation<useCustomVocabularySettingValidateCustomVocabularyListMutation>(validateCustomVocabularyListMutation);

	const [inputText, setInputText] = React.useState("");

	const [errorMessage, setErrorMessage] = React.useState<string | null>(null);

	const onToggleCustomVocabularyEnabled = React.useCallback(
		(enabled: boolean) => {
			setIsCustomVocabularyEnabled(enabled);
		},
		[setIsCustomVocabularyEnabled],
	);

	const onDeleteCustomVocabularyWord = React.useCallback(
		(wordToDelete: string) => {
			if (customVocabularyList) {
				setCustomVocabularyList(customVocabularyList.filter(word => word !== wordToDelete));
			}
		},
		[customVocabularyList, setCustomVocabularyList],
	);

	const onInputChange = React.useCallback(
		(e: React.ChangeEvent<HTMLInputElement>) => {
			if (errorMessage) {
				setErrorMessage(null);
			}
			setInputText(e.target.value);
		},
		[errorMessage],
	);

	const onAddCustomVocabularyWord = React.useCallback(
		async (e: React.FormEvent<HTMLFormElement>) => {
			e.preventDefault();
			const filteredWords = inputText
				.split(",")
				.map(word => word.trim())
				.filter(Boolean); // removes all falsy values (empty strings, null, undefined, etc.)

			const updatedCustomVocabularyList = [...(customVocabularyList ?? []), ...filteredWords];

			// added this validation here so we don't send a large payload to the server
			if (updatedCustomVocabularyList.length > CUSTOM_VOCABULARY_WORD_LIMIT) {
				setErrorMessage(
					`You've reached the limit of ${CUSTOM_VOCABULARY_WORD_LIMIT} words. Please remove some to add new ones.`,
				);
				return;
			}
			validateCustomVocabularyList({
				variables: {
					customVocabularyList: updatedCustomVocabularyList,
				},
				onCompleted({ validateCustomVocabularyList: response }) {
					if (response?.valid) {
						if (errorMessage) {
							setErrorMessage(null);
						}
						setCustomVocabularyList(updatedCustomVocabularyList);
						setInputText("");
					} else if (response?.error) {
						setErrorMessage(response.error);
					}
				},
				onError() {
					setErrorMessage("Unable to validate custom vocabulary list.");
				},
			});
		},
		[inputText, customVocabularyList, validateCustomVocabularyList, errorMessage, setCustomVocabularyList],
	);

	return {
		isCustomVocabularyEnabled,
		customVocabularyList,
		isValidatingCustomVocabularyList,
		errorMessage,
		onToggleCustomVocabularyEnabled,
		onDeleteCustomVocabularyWord,
		onAddCustomVocabularyWord,
		inputText,
		onInputChange,
	};
};
